import { client } from 'src/api/main/client.gen'

/**
 * Thin local wrapper around /account/notification-preferences/.
 * Replace with generated calls after `npm run openapi` once the backend
 * schema is deployed.
 *
 * Matrix shape mirrors the Notification service:
 *   { app_code: { category: [channels] } }
 * A channel listed under a category means the user has *opted out* of that
 * (category, channel) pair. The special category key `*` (wildcard) means
 * "every category emitted by this app" — set by the Notification service so
 * a single toggle can cover the whole CRM / HRM bucket.
 */

export const NOTIFICATION_WILDCARD = '*' as const

export type NotificationChannel = 'email' | 'push' | 'internal'

export const NOTIFICATION_CHANNELS: NotificationChannel[] = [
  'email',
  'push',
  'internal'
]

/**
 * Rows displayed on the preferences page. Each row points at a real
 * (app_code, category) pair. Use the wildcard category for app-level groups.
 *
 * `channels` lists the delivery methods that actually emit for this row, so
 * the UI hides toggles for channels the backend doesn't use. Core's direct
 * emails (security, invite, support, audit) only ship via email; role events
 * are emitted as in-app notifications via the Notification service; CRM/HRM
 * buckets carry many categories with email/push/in-app templates.
 */
export type PreferenceRow = {
  /** i18n key suffix under `account.notifications.preferences.rows.*`. */
  key: string
  appCode: string
  category: string
  channels: NotificationChannel[]
}

export const PREFERENCE_ROWS: PreferenceRow[] = [
  {
    key: 'roleChanged',
    appCode: 'MAIN',
    category: 'MAIN_role_changed',
    channels: ['internal']
  },
  {
    key: 'roleReplaced',
    appCode: 'MAIN',
    category: 'MAIN_role_replaced',
    channels: ['internal']
  },
  {
    key: 'securityNotification',
    appCode: 'MAIN',
    category: 'MAIN_security_notification',
    channels: ['email']
  },
  {
    key: 'workspaceInvite',
    appCode: 'MAIN',
    category: 'MAIN_workspace_invite',
    channels: ['email']
  },
  {
    key: 'supportReply',
    appCode: 'MAIN',
    category: 'MAIN_support_reply',
    channels: ['email']
  },
  {
    key: 'auditExport',
    appCode: 'MAIN',
    category: 'MAIN_audit_export',
    channels: ['email']
  },
  {
    key: 'crmActivity',
    appCode: 'CRM',
    category: NOTIFICATION_WILDCARD,
    channels: ['email', 'push', 'internal']
  },
  {
    key: 'hrmActivity',
    appCode: 'HRM',
    category: NOTIFICATION_WILDCARD,
    channels: ['email', 'push', 'internal']
  }
]

export type DisabledCategoriesMatrix = Record<
  string,
  Record<string, NotificationChannel[]>
>

export type NotificationPreferencesResponse = {
  disabled_categories: DisabledCategoriesMatrix
}

type NotificationPreferencesGetResponses = {
  200: NotificationPreferencesResponse
}
type NotificationPreferencesPutResponses = {
  200: NotificationPreferencesResponse
}

export const fetchNotificationPreferences = () =>
  client.get<NotificationPreferencesGetResponses, unknown, true>({
    url: '/account/notification-preferences/',
    responseType: 'json',
    throwOnError: true
  })

export const updateNotificationPreferences = (
  disabledCategories: DisabledCategoriesMatrix
) =>
  client.put<NotificationPreferencesPutResponses, unknown, true>({
    url: '/account/notification-preferences/',
    body: { disabled_categories: disabledCategories },
    responseType: 'json',
    throwOnError: true
  })
